import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useActingAthlete } from '../context/ActingAthleteContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  EditorHero,
  MonoLabel,
  Monogram,
  SectionCap,
  TaskCard,
  EditorialButton,
} from '../components/Editorial';
import DeleteAthleteModal from '../components/DeleteAthleteModal';
import { isAthlete as isAthleteRole } from '../utils/roles';
import Button from '../components/Button';
import AdminDashboard from './AdminDashboard';
import PartnerDashboard from './PartnerDashboard';
import { personalFieldsFor } from '../utils/athleteCompletion';

const ATHLETIC_FIELDS = [
  { key: 'position', label: 'Primary Position' },
  { key: 'position_primary', label: 'Football Position', alt: 'position_secondary' },
  { key: 'sports_played', label: 'Sports', isArray: true },
];

const VERIFIABLE_FIELDS = [
  'height_inches', 'weight_lbs', 'wingspan', 'hand_size',
  'dash_40', 'run_10', 'run_20', 'short_shuttle',
  'three_cone_drill', 'broad_jump', 'vertical_jump',
  'bench_press', 'squat', 'clean', 'deadlift',
  'act_score', 'sat_score',
];

const COACH_FIELDS = [
  'school_name', 'position_title', 'sport', 'division',
  'cell_phone', 'home_address', 'player_photo', 'personal_statement',
];

/* ── SVG Icons ────────────────────────────────────────────── */

const IconEye = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const IconTrophy = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const IconUsers2 = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconRevenue = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const IconAthlete = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M12 8v8" /><path d="M8 12h8" />
  </svg>
);

const IconCoach = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

const IconPartner = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><polyline points="17 11 19 13 23 9" />
  </svg>
);

const IconPending = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconSearch = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconMessage = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const IconShield = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

/* ── Circular Progress Ring Component ── */
const ProgressRing = ({ pct, size = 90 }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <svg className="dash-ring__svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <linearGradient id="ring-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#4D8064" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
      </defs>
      <circle className="dash-ring__track" cx={size/2} cy={size/2} r={radius} />
      <circle
        className="dash-ring__fill"
        cx={size/2} cy={size/2} r={radius}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
      />
      <text className="dash-ring__pct" x={size/2} y={size/2}>{pct}%</text>
    </svg>
  );
};

/* ── Time-based Greeting ── */
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

export default function DashboardPage() {
  const { user, API_URL } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  // Billing status (does this athlete have a subscription row?), NOT an
  // entitlement (what may they do?) — the two questions look interchangeable
  // but aren't: `null` means "not yet fetched", an object with
  // `current_period_end` set means a real subscription row exists (paid or
  // comped), and `current_period_end: null` means the athlete has never
  // subscribed. See the GENEROUS-failure note on getSubLabel() below for why
  // fetch failure is folded into "has a subscription" rather than tracked
  // separately.
  const [subscription, setSubscription] = useState(null);
  const [subscriptionFetchFailed, setSubscriptionFetchFailed] = useState(false);
  const [coachProfile, setCoachProfile] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [message, setMessage] = useState('');

  const isAdmin = user && (user.role === 'admin' || user.role === 'internal_staff');
  const isCoach = user && user.role === 'coach';
  const isPartner = user && user.role === 'partner';
  const isAthlete = user && isAthleteRole(user.role);
  const isGuardian = user && user.role === 'guardian';

  const { actingAthleteId, actingAthleteName, enterAthlete, exitAthlete } = useActingAthlete();
  // True when the dashboard should render athlete-shaped content,
  // either because the signed-in user IS an athlete or because a
  // guardian is currently acting on a linked athlete (which auto-
  // injects ?as_athlete_id= into every /api call).
  const showAthleteUI = isAthlete || (isGuardian && actingAthleteId !== null);

  const [managedAthletes, setManagedAthletes] = useState([]);
  const [athleteToDelete, setAthleteToDelete] = useState(null);

  useEffect(() => {
    // The marketing role used to be sent to /marketing/dashboard here. That page's data service moved
    // off this server (2026-10-15), so a marketing account now stays on /dashboard.
    // AdminDashboard and PartnerDashboard each fetch their own data, so
    // admins and partners don't need this page's loading gate held open
    // for them.
    if (!(user && (isAdmin || isPartner))) {
      setLoading(false);
    }
    if (user && showAthleteUI) {
      // Athlete profile fetch fires for direct athletes AND for guardians
      // acting on a linked athlete — the axios interceptor injects
      // as_athlete_id when an acting context is set, so /api/athletes/profile
      // and /api/users/me/subscription return the athlete's data either way.
      fetchProfile();
      fetchSubscription();
    }
    if (user && isCoach) {
      fetchCoachProfile();
    }
    if (user && isGuardian) {
      fetchManagedAthletes();
    }
  }, [user, actingAthleteId]);  // re-fetch when guardian enters/exits an athlete

  // Auto-enter when a guardian has exactly one linked athlete and isn't
  // already acting — drops them straight into the athlete's view (same
  // skip-the-picker UX as the iOS app).
  useEffect(() => {
    if (
      isGuardian &&
      actingAthleteId === null &&
      managedAthletes.length === 1
    ) {
      enterAthlete(managedAthletes[0]);
    }
  }, [isGuardian, actingAthleteId, managedAthletes, enterAthlete]);

  const fetchManagedAthletes = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/guardians/my-athletes`);
      setManagedAthletes(data || []);
    } catch (err) {
      console.error('Failed to load managed athletes', err);
      setManagedAthletes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setShowContactModal(false); };
    if (showContactModal) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showContactModal]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/athletes/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  // Billing status only — do not read `status` off this response (it's
  // faked to "active" for everyone while PAYMENTS_REQUIRED=false; see
  // backend/app/routers/users.py get_my_subscription). `current_period_end`
  // is the honest signal: set for any user with a subscription row (paid or
  // comped), null only when no row exists at all.
  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/me/subscription`);
      setSubscription(response.data);
      setSubscriptionFetchFailed(false);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscriptionFetchFailed(true);
    }
  };

  const fetchCoachProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/coaches/profile`);
      setCoachProfile(response.data);
    } catch (error) {
      console.error('Error fetching coach profile:', error);
    }
  };

  /* ── Helpers ──────────────────────────────────────────── */

  const getInitials = (first, last) => {
    return ((first || '')[0] || '') + ((last || '')[0] || '');
  };

  const getAthletePhotoUrl = () => {
    if (!profile) return null;
    const idx = profile.primary_photo_index || 1;
    const photoKey = idx === 1 ? 'player_photo' : `player_photo_${idx}`;
    return profile[photoKey] || profile.player_photo || null;
  };

  const countFilled = (fields, data) => {
    if (!data) return { filled: 0, total: fields.length };
    let filled = 0;
    for (const field of fields) {
      if (field.isArray) {
        const arr = data[field.key];
        if (Array.isArray(arr) && arr.length > 0) filled++;
      } else {
        const val = data[field.key];
        const altVal = field.alt ? data[field.alt] : null;
        if (val || altVal) filled++;
      }
    }
    return { filled, total: fields.length };
  };

  const getAthleteCompletionPct = () => {
    if (!profile) return 0;
    const personal = countFilled(personalFieldsFor(profile), profile);
    const athletic = countFilled(ATHLETIC_FIELDS, profile);
    const verifiablePresent = VERIFIABLE_FIELDS.filter(k => profile[k]).length;
    const totalFields = personal.total + athletic.total + VERIFIABLE_FIELDS.length;
    const filledFields = personal.filled + athletic.filled + verifiablePresent;
    return totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
  };

  const getCoachCompletionPct = () => {
    if (!coachProfile) return 0;
    const filled = COACH_FIELDS.filter(k => coachProfile[k]).length;
    return Math.round((filled / COACH_FIELDS.length) * 100);
  };

  const formatPosition = () => {
    if (!profile) return '';
    const parts = [profile.position_primary, profile.position_secondary].filter(Boolean);
    return parts.join(' / ');
  };

  const formatClassYear = () => {
    if (!profile?.graduation_year) return '';
    return "'" + profile.graduation_year.toString().slice(-2);
  };

  const formatSchool = () => {
    if (!profile) return '';
    const parts = [profile.school ?? profile.high_school];
    const loc = [profile.city, profile.state].filter(Boolean).join(', ');
    if (loc) parts.push(loc);
    return parts.filter(Boolean).join(' - ');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // This is a BILLING STATUS label ("does this account have a subscription
  // row?"), not an entitlement ("what may this account do?"). It must NOT
  // come from useEntitlements().tier: PAYMENTS_REQUIRED=false makes `tier`
  // resolve to 'pro' for every account, subscribed or not, which is correct
  // for access decisions but wrong for a label describing billing state.
  //
  // Do not key this on subscription.status either — GET
  // /users/me/subscription (backend/app/routers/users.py) short-circuits to
  // status:"active" for everyone while PAYMENTS_REQUIRED is false, the same
  // fake-positive problem as `tier`. `current_period_end` is the one field
  // that endpoint does NOT fake: populated only when a subscription row
  // exists (paid or comped), null when it doesn't.
  //
  // GENEROUS by construction, same direction as every other consumer in this
  // codebase: `subscriptionFetchFailed` and the pre-fetch `subscription ===
  // null` state both fall through to 'active' — a network blip or a load
  // still in flight must never badge a paying athlete "Free Tier" or nag
  // them with the upgrade banner. Only a resolved response confirming no
  // subscription row (current_period_end: null) yields 'free'.
  const getSubLabel = () => {
    if (subscriptionFetchFailed || !subscription) return 'active';
    return subscription.current_period_end ? 'active' : 'free';
  };

  const getRoleBadge = () => {
    if (isAthlete) {
      const sub = getSubLabel();
      return sub === 'active'
        ? { label: 'Active', cls: 'dash-hero__badge--active' }
        : { label: 'Free Tier', cls: 'dash-hero__badge--free' };
    }
    if (user?.role === 'coach') return { label: 'Coach', cls: 'dash-hero__badge--coach' };
    if (user?.role === 'partner') return { label: 'Partner', cls: 'dash-hero__badge--partner' };
    if (isAdmin) return { label: user.role === 'admin' ? 'Admin' : 'Staff', cls: 'dash-hero__badge--admin' };
    return null;
  };

  /* ── Render: Hero Profile Card (shared) ──────────────── */

  const renderHeroCard = (photoUrl, name, metaLines, badge) => (
    <div className="dash-hero">
      <div className="dash-hero__banner" />
      <div className="dash-hero__body">
        <div className="dash-hero__avatar-wrap">
          {photoUrl ? (
            <img src={photoUrl} alt="Profile" className="dash-hero__photo" />
          ) : (
            <div className="dash-hero__initials">
              {getInitials(user.first_name, user.last_name)}
            </div>
          )}
        </div>
        {badge && (
          <span className={`dash-hero__badge ${badge.cls}`}>{badge.label}</span>
        )}
        <h2 className="dash-hero__name">{name}</h2>
        {metaLines.map((line, i) => (
          <div key={i} className={i === 0 ? 'dash-hero__meta' : 'dash-hero__detail'}>{line}</div>
        ))}
      </div>
    </div>
  );

  /* ── Render: Athlete Dashboard (Editorial / Slate) ──── */

  const formatHeight = (inches) => {
    if (!inches) return null;
    // Round to whole inches BEFORE dividing — flooring first then rounding the
    // remainder renders 71.6 as 5'12" instead of 6'0".
    const total = Math.round(inches);
    return `${Math.floor(total / 12)}'${total % 12}"`;
  };

  const formatVertical = (val) => (val ? `${val}"` : null);

  const renderAthleteDashboard = () => {
    const pct = getAthleteCompletionPct();
    const sport = (profile?.sports_played || []).join(', ');
    const positions = formatPosition();
    const sportLine = [sport, positions].filter(Boolean).join(' · ');
    const subParts = [];
    if (profile?.graduation_year) subParts.push(`Class of ${formatClassYear()}`);
    if (profile?.school ?? profile?.high_school) subParts.push(formatSchool());
    if (sportLine) subParts.push(sportLine);
    const subLine = subParts.join(' · ');

    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    const roleLabel = getSubLabel() === 'active' ? 'ATHLETE · ACTIVE' : 'ATHLETE · FREE TIER';

    const accent = pct >= 100 ? 'ready.' : 'underway.';
    const headlineLead = pct >= 100
      ? `${user.first_name}, you're`
      : `${user.first_name}, your recruiting journey is`;

    const stats = [
      { label: 'HEIGHT', value: formatHeight(profile?.height_inches) },
      { label: 'WEIGHT', value: profile?.weight_lbs ? `${profile.weight_lbs}` : null },
      { label: '40-YARD', value: profile?.dash_40 || null },
      { label: 'VERTICAL', value: formatVertical(profile?.vertical_jump) },
      { label: 'GPA', value: profile?.gpa_weighted || profile?.gpa_unweighted || null },
    ];

    const subLabel = getSubLabel();
    const showUpgradeBanner = subLabel !== 'active';

    return (
      <div className="ed-page">
        {showUpgradeBanner && (
          <div
            onClick={() => navigate('/upgrade')}
            style={{
              background: 'linear-gradient(135deg, #0066CC 0%, #1e40af 100%)',
              color: 'white',
              padding: '16px 20px',
              borderRadius: 8,
              marginBottom: 16,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.85 }}>
                Your profile is live in coach search
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
                See what Pro adds →
              </div>
            </div>
            <div style={{ background: 'white', color: '#0066CC', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
              View plans
            </div>
          </div>
        )}
        <EditorHero
          eyebrow={
            <>
              <MonoLabel onDark>{today.toUpperCase()}</MonoLabel>
              <MonoLabel onDark>·</MonoLabel>
              <MonoLabel onDark>{roleLabel}</MonoLabel>
            </>
          }
          headline={headlineLead}
          accent={accent}
          sub={subLine || 'Your recruiting hub. Verified data, live coach reach, ready to share.'}
          actions={
            <>
              <EditorialButton variant="solid" onClick={() => navigate('/profile')}>
                {pct >= 100 ? 'View Profile' : 'Complete Profile'}
              </EditorialButton>
              <EditorialButton variant="ghost" onClick={() => navigate('/camps')}>
                Find a Camp
              </EditorialButton>
            </>
          }
          stats={stats}
        />

        {/* "Connect your family" CTA — visible to any athlete-side account
            (including a fresh parent who defaulted to role=ATHLETE at
            signup) as well as a guardian currently acting on a linked
            athlete. Hidden for coach/partner/admin/staff, who never reach
            renderAthleteDashboard. */}
        <div style={{ marginTop: 12, marginBottom: 12 }}>
          <Link to="/connect-athlete" className="ed-btn ed-btn--solid">
            Connect your athlete
          </Link>
        </div>

        {/* Profile completion + visibility metrics */}
        <SectionCap label="This Week" hint={`${pct}% PROFILE`} />
        <div className="ed-grid ed-grid--2-1">
          <div className="ed-card ed-card--ink">
            <MonoLabel onDark>PROFILE STRENGTH</MonoLabel>
            <h3 className="ed-card__title ed-card__title--lg">
              {pct >= 100 ? 'Your profile is fully built out.' : `${pct}% complete — keep going.`}
            </h3>
            <p className="ed-card__body">
              {pct >= 100
                ? 'Keep your stats fresh and verified to stay at the top of coach search.'
                : 'A complete profile is what coaches see first. Add what\'s missing and we\'ll prioritize you in search.'}
            </p>
            <div className="ed-card__actions">
              {pct < 100 ? (
                <EditorialButton variant="solid" onClick={() => navigate('/profile')}>
                  Continue Building
                </EditorialButton>
              ) : (
                <EditorialButton variant="ghost" onClick={() => navigate('/profile')}>
                  View Profile
                </EditorialButton>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="ed-card">
              <MonoLabel>COACH VIEWS</MonoLabel>
              <div className="ed-card__title ed-card__title--lg" style={{ fontFamily: 'var(--ed-font-serif)' }}>
                {profile?.coach_views || 0}
              </div>
            </div>
            <div className="ed-card">
              <MonoLabel>PROFILE VIEWS</MonoLabel>
              <div className="ed-card__title ed-card__title--lg" style={{ fontFamily: 'var(--ed-font-serif)' }}>
                {profile?.profile_views || 0}
              </div>
            </div>
            <div className="ed-card">
              <MonoLabel>OFFERS</MonoLabel>
              <div className="ed-card__title ed-card__title--lg" style={{ fontFamily: 'var(--ed-font-serif)' }}>
                {profile?.offer_count || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Next steps — verification education lives here */}
        <SectionCap label="Next Steps" hint="GET RECRUITED" />
        <div className="ed-grid ed-grid--1-1">
          <TaskCard
            cap="RECRUITING"
            title="Review your school matches"
            body="See the colleges that best fit your preferences — major, region, size, division and more — ranked for you."
            iconVariant="green"
            icon={<IconSearch />}
            onClick={() => navigate('/recruit?mode=schools&tab=matches')}
          />
          <TaskCard
            cap="VERIFICATION"
            title="Get Verified"
            body={`Coaches are 73% more likely to make an offer with verified data${profile?.last_verified_at ? ` — last verified ${formatDate(profile.last_verified_at)}` : ''}.`}
            iconVariant="green"
            icon={<IconShield />}
            onClick={() => navigate('/camps')}
          />
          <TaskCard
            cap="PARTNER NETWORK"
            title="Find a Certified Partner"
            body="Contact ProZpects to locate a partner near you who can verify your stats."
            iconVariant="purple"
            icon={<IconMail />}
            onClick={() => {
              setContactForm({ name: user ? `${user.first_name} ${user.last_name}` : '', email: user?.email || '', message: '' });
              setShowContactModal(true);
            }}
          />
        </div>
      </div>
    );
  };

  /* ── Render: Coach / Partner Dashboard ──────────────── */

  const renderCoachDashboard = () => {
    const cp = coachProfile || {};
    const photoUrl = cp.player_photo || null;
    const pct = getCoachCompletionPct();
    const initials = ((user?.first_name || '')[0] || '') + ((user?.last_name || '')[0] || '');

    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    const roleLabel =
      user?.role === 'partner'      ? 'PARTNER' :
      user?.role === 'basic_coach'  ? 'COACH · PENDING REVIEW' :
                                      'COACH';

    const subParts = [];
    if (cp.school_name) subParts.push(cp.school_name);
    if (cp.position_title) subParts.push(cp.position_title);
    if (cp.sport) subParts.push(cp.sport);
    const subLine = subParts.join(' · ') || 'Your recruiting hub. Search athletes, message prospects, verify data.';

    const accent = pct >= 100 ? 'set.' : 'in motion.';
    const headlineLead = pct >= 100
      ? `${user.first_name}, you're`
      : `${user.first_name}, the day is`;

    return (
      <div className="ed-page">
        {user?.role === 'basic_coach' && (
          <div className="ed-card" style={{ borderLeft: '3px solid var(--ed-accent-yellow)', background: '#fffbf0' }}>
            <MonoLabel accent>APPLICATION UNDER REVIEW</MonoLabel>
            <p className="ed-card__body" style={{ marginTop: 4 }}>
              Your request to be a ProZpects coach is pending review of your school credentials and background check.
              You'll receive an email once your account has been approved.
            </p>
          </div>
        )}

        <EditorHero
          eyebrow={
            <>
              <MonoLabel onDark>{today.toUpperCase()}</MonoLabel>
              <MonoLabel onDark>·</MonoLabel>
              <MonoLabel onDark>{roleLabel}</MonoLabel>
            </>
          }
          prefix={<Monogram initials={initials} photoUrl={photoUrl} />}
          headline={headlineLead}
          accent={accent}
          sub={subLine}
        />

        <SectionCap label="Your Profile" hint={`${pct}% COMPLETE`} />
        <div className="ed-grid ed-grid--2-1">
          <div className="ed-card ed-card--ink">
            <MonoLabel onDark>PROFILE STRENGTH</MonoLabel>
            <h3 className="ed-card__title ed-card__title--lg">
              {pct >= 100 ? 'Your profile is fully built out.' : `${pct}% complete — keep going.`}
            </h3>
            <p className="ed-card__body">
              {pct >= 100
                ? 'Athletes and families trust complete profiles. Keep yours fresh.'
                : 'A complete profile helps athletes and families trust your outreach.'}
            </p>
            <div className="ed-card__actions">
              {pct < 100 ? (
                <EditorialButton variant="solid" onClick={() => navigate('/coach-profile')}>
                  Continue Building
                </EditorialButton>
              ) : (
                <EditorialButton variant="ghost" onClick={() => navigate('/coach-profile')}>
                  View Profile
                </EditorialButton>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="ed-card">
              <MonoLabel>SCHOOL</MonoLabel>
              <div style={{ fontFamily: 'var(--ed-font-serif)', fontSize: 22, fontWeight: 500, marginTop: 4 }}>
                {cp.school_name || '—'}
              </div>
            </div>
            <div className="ed-card">
              <MonoLabel>POSITION</MonoLabel>
              <div style={{ fontFamily: 'var(--ed-font-serif)', fontSize: 22, fontWeight: 500, marginTop: 4 }}>
                {cp.position_title || '—'}
              </div>
            </div>
            <div className="ed-card">
              <MonoLabel>SPORT</MonoLabel>
              <div style={{ fontFamily: 'var(--ed-font-serif)', fontSize: 22, fontWeight: 500, marginTop: 4 }}>
                {cp.sport || '—'}
              </div>
            </div>
          </div>
        </div>

        <SectionCap label="Quick Actions" hint="GO" />
        <div className="ed-grid ed-grid--1-1">
          <TaskCard
            cap="DISCOVERY"
            title="Search Athletes"
            body="Browse verified athletes filtered by position, region, and recruiting class."
            iconVariant="green"
            icon={<IconSearch />}
            onClick={() => navigate('/players')}
          />
          <TaskCard
            cap="OUTREACH"
            title="Messages"
            body="Reach out to prospects and respond to inbound interest."
            iconVariant="purple"
            icon={<IconMessage />}
            onClick={() => navigate('/messages')}
          />
        </div>
      </div>
    );
  };


  /* ── Main Render ────────────────────────────────────── */

  return (
    <>
      {message && (
        <div className="settings-toast" role="alert">{message}</div>
      )}

      {/* Banner across the top whenever a guardian is acting on a linked
          athlete — the web equivalent of the iOS corner ribbon. Persistent
          'Acting as <name> · Exit' affordance ensures the user always sees
          which mode they're in and how to back out. */}
      {isGuardian && actingAthleteId !== null && (
        <div
          style={{
            background: 'linear-gradient(135deg, #C82A2A, #8E1010)',
            color: '#fff',
            padding: '10px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: 0.3,
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
          role="status"
        >
          <span>
            <span style={{ opacity: 0.85, marginRight: 8 }}>GUARDIAN MODE</span>
            Acting as {actingAthleteName || 'athlete'}
          </span>
          <Button type="button" variant="secondary" size="sm" onClick={exitAthlete}>
            Back to my athletes
          </Button>
        </div>
      )}

      {showAthleteUI && renderAthleteDashboard()}

      {isCoach && renderCoachDashboard()}

      {isGuardian && actingAthleteId === null && (
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '32px 20px' }}>
          <div className="dash-greeting">
            <h1 className="dash-greeting__text">
              {getGreeting()}, {user?.first_name || 'Guardian'}
            </h1>
            <p className="dash-greeting__sub">
              {managedAthletes.length === 0
                ? 'No linked athletes yet — once an athlete invites you and you accept, they\'ll appear here.'
                : `Managing ${managedAthletes.length} ${managedAthletes.length === 1 ? 'athlete' : 'athletes'}`}
            </p>
            <div style={{ marginTop: 12 }}>
              <Link to="/connect-athlete" className="ed-btn ed-btn--solid">
                Connect your athlete
              </Link>
            </div>
          </div>

          {managedAthletes.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 20 }}>
              {managedAthletes.map((a) => (
                <div
                  key={a.relationship_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: 16,
                    border: '1px solid var(--ed-color-rule, #e5e7eb)',
                    borderRadius: 12,
                    background: 'var(--ed-color-bg, #fff)',
                    width: '100%',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => enterAthlete(a)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, flex: 1,
                      background: 'transparent', border: 'none', padding: 0,
                      cursor: 'pointer', textAlign: 'left', font: 'inherit',
                    }}
                  >
                    <div
                      style={{
                        width: 48, height: 48, borderRadius: '50%',
                        background: 'var(--ed-color-accent, #0066cc)' + '20',
                        color: 'var(--ed-color-accent, #0066cc)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 17,
                      }}
                    >
                      {((a.athlete_first_name || '')[0] || '') + ((a.athlete_last_name || '')[0] || '')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 16 }}>
                        {[a.athlete_first_name, a.athlete_last_name].filter(Boolean).join(' ') || a.athlete_email}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--ed-color-muted, #64748b)', marginTop: 4 }}>
                        {a.permission === 'edit' ? 'Edit access' : 'View only'}
                        {a.relation ? ` · ${a.relation}` : ''}
                      </div>
                    </div>
                    <span style={{ color: 'var(--ed-color-muted, #64748b)', fontSize: 18 }}>›</span>
                  </button>
                  {a.permission === 'edit' && (
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      iconOnly
                      onClick={() => setAthleteToDelete(a)}
                      title="Delete athlete"
                      aria-label="Delete athlete"
                      style={{ fontSize: 22 }}
                    >
                      ⋯
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isAdmin && <AdminDashboard user={user} />}

      {isPartner && <PartnerDashboard />}

      {/* Contact Modal */}
      {showContactModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="contact-modal-title" onClick={() => setShowContactModal(false)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowContactModal(false)}>&times;</button>
            <div className="modal-title" id="contact-modal-title">Contact ProZpects</div>

            <div className="form-field" style={{ marginBottom: '12px' }}>
              <label className="form-field__label">Name *</label>
              <input
                type="text"
                className="form-field__input"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              />
            </div>
            <div className="form-field" style={{ marginBottom: '12px' }}>
              <label className="form-field__label">Email *</label>
              <input
                type="email"
                className="form-field__input"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              />
            </div>
            <div className="form-field" style={{ marginBottom: '12px' }}>
              <label className="form-field__label">Message</label>
              <textarea
                className="form-field__input"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                rows={4}
                style={{ resize: 'vertical' }}
                placeholder="Tell us what you're looking for..."
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <Button variant="primary" onClick={async () => {
                try {
                  const config = { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } };
                  await axios.post(`${API_URL}/contact/partner-inquiry`, contactForm, config);
                  setShowContactModal(false);
                  setMessage('Your inquiry has been submitted!');
                  setTimeout(() => setMessage(''), 3000);
                } catch (error) {
                  setMessage(error.response?.data?.detail || 'Error submitting inquiry');
                  setTimeout(() => setMessage(''), 4000);
                }
              }}>
                Submit
              </Button>
              <Button variant="secondary" onClick={() => setShowContactModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {athleteToDelete && (
        <DeleteAthleteModal
          athlete={athleteToDelete}
          onClose={() => setAthleteToDelete(null)}
          onDeleted={() => {
            setAthleteToDelete(null);
            fetchManagedAthletes();
          }}
        />
      )}
    </>
  );
}
